/**
 * `/login/enroll`: first sign-in of a staff member without a second factor (§17.1 "MFA for staff", D-2205). The
 * API answered `MFA_ENROLLMENT_REQUIRED` and holds a pending-MFA session; this page creates the TOTP secret on
 * that session, shows it once (QR + manual key, never logged), and confirms the first code, which completes the
 * login and continues to the requested path.
 */
import { Button, Field, Input, Skeleton, useIntl } from '@windrop/ui';
import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { identityApi, type TotpEnrollmentDto } from '../../lib/api/admin';
import { isApiError } from '../../lib/api/client';
import { AuthLayout } from './AuthLayout';
import { useContinueLogin, useLoginFlow } from './Verify';

/** Groups the base32 key in fours so it can be typed into an authenticator app. */
export const formatManualKey = (key: string): string =>
  key
    .replace(/\s+/g, '')
    .replace(/(.{4})/g, '$1 ')
    .trim();

export function EnrollPage() {
  const flow = useLoginFlow();
  const continueLogin = useContinueLogin();
  const intl = useIntl();
  const [enrolment, setEnrolment] = useState<TotpEnrollmentDto | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const hasFlow = Boolean(flow?.code);

  useEffect(() => {
    if (!hasFlow) return;
    let cancelled = false;
    setLoadError(null);
    identityApi
      .totpEnroll()
      .then((e) => {
        if (!cancelled) setEnrolment(e);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLoadError(
          isApiError(err)
            ? (err.problem?.detail ?? err.message)
            : 'Could not prepare an authenticator secret. Start the sign-in again.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [hasFlow, attempt]);

  if (!flow || !flow.code) {
    return (
      <AuthLayout title="Sign-in step missing">
        <p>Your sign-in link must be verified first.</p>
        <Button href="/login">Back to sign in</Button>
      </AuthLayout>
    );
  }

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      setError('Enter the 6-digit code your authenticator app shows for Windrop.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      continueLogin(await identityApi.totpConfirm(flow.challengeId, code), flow, flow.code ?? '');
    } catch (err) {
      setError(
        isApiError(err)
          ? (err.problem?.detail ?? err.message)
          : 'That code was not accepted. Codes rotate every 30 seconds.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthLayout title="Set up your authenticator">
      <p>
        Staff sign-in needs a second factor (§17.1). Scan the code with an authenticator app (or type the
        key), then enter the 6-digit code it shows to finish signing in.
      </p>
      {loadError ? (
        <>
          <p role="alert" className="text-error">
            {loadError}
          </p>
          <div className="wd-btn-group">
            <Button onClick={() => setAttempt((n) => n + 1)}>Try again</Button>
            <Button href="/login" variant="secondary">
              Back to sign in
            </Button>
          </div>
        </>
      ) : enrolment ? (
        <div className="wd-stack" data-testid="totp-enrolment">
          <img
            src={enrolment.qrDataUrl}
            alt={`QR code to enrol ${enrolment.accountLabel} in your authenticator app`}
            width={192}
            height={192}
            style={{ alignSelf: 'center', background: '#fff', padding: 8, borderRadius: 8 }}
          />
          <dl className="wdc-facts">
            <div>
              <dt>Account</dt>
              <dd>
                {enrolment.issuer} · {enrolment.accountLabel}
              </dd>
            </div>
            <div>
              <dt>Manual key</dt>
              <dd>
                <code data-testid="totp-manual-key">{formatManualKey(enrolment.manualKey)}</code>
              </dd>
            </div>
            <div>
              <dt>Set-up expires</dt>
              <dd>{intl.formatDateTime(enrolment.expiresAt)}</dd>
            </div>
          </dl>
        </div>
      ) : (
        <Skeleton shape="block" height={220} label="Preparing your authenticator secret" />
      )}
      <form onSubmit={submit} className="wd-stack" noValidate>
        <Field label="6-digit code" required hint="From your authenticator app, after scanning.">
          <Input
            preset="otp"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            maxLength={6}
            disabled={!enrolment}
            data-testid="totp-enrol-code"
          />
        </Field>
        {error ? (
          <p role="alert" className="text-error">
            {error}
          </p>
        ) : null}
        <Button type="submit" loading={busy} loadingLabel="Confirming" disabled={!enrolment} block>
          Confirm and finish sign-in
        </Button>
        <p className="wd-muted">
          Problem with the app? <Link to="/login">Start again</Link> or ask your organizer admin.
        </p>
      </form>
    </AuthLayout>
  );
}
